import React, { useEffect, useState } from "react";
import axios from "axios";
import config from "../Config/config"; // adjust path if needed
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const AnnouncementsEmailEditor = () => {
  const userEmail = localStorage.getItem("email");
  const [announcement, setAnnouncement] = useState(localStorage.getItem("announcement") || "");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [users, setUsers] = useState([]);
  const [lnUsers, setLnUsers] = useState([]);
  const [progress, setProgress] = useState({ sent: 0, total: 0 });
  const [errorLog, setErrorLog] = useState([]);
  const [subject, setSubject] = useState(localStorage.getItem("announcementSubject") || "📢 Listernaut Announcement");
  const [logoUrl, setLogoUrl] = useState(localStorage.getItem("announcementLogo") || "https://storage.googleapis.com/listalotimages/emailtemplates/logo.png");
  const [assets, setAssets] = useState([]);

  const announcementCustomArgs = {
    listernaut_email_type: "announcement",
  };

  // Persist inputs
  useEffect(() => { localStorage.setItem("announcement", announcement); }, [announcement]);
  useEffect(() => { localStorage.setItem("announcementSubject", subject); }, [subject]);
  useEffect(() => { localStorage.setItem("announcementLogo", logoUrl); }, [logoUrl]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const res = await axios.get(`${config.API_BASE_URL}/get-all-users`);
        const arr = Array.isArray(res.data) ? res.data : [];
        setUsers(arr);
      } catch (e) {
        console.error("Failed to load users", e);
      }
    };
    loadUsers();
  }, []);

useEffect(() => {
  const loadLnUsers = async () => {
    try {
      const res = await axios.get("https://listernautapi.azurewebsites.net/api/v2/Listalot/users");
      if (Array.isArray(res.data)) {

        // Convert firebase users to lowercase email array
        const firebaseEmails = users
          .filter(u => u?.email)
          .map(u => String(u.email).toLowerCase());

        // Remove common users same as WhatsNew logic
        const filtered = res.data.filter(u => {
          const email = u?.email ? String(u.email).toLowerCase() : "";
          return email && !firebaseEmails.includes(email);
        });

        setLnUsers(filtered);
      }
    } catch (e) {
      console.error("Failed to load LN users", e);
    }
  };
  loadLnUsers();
}, [users]);

  useEffect(() => {
    const loadAssets = async () => {
      try {
        const res = await axios.get(`${config.API_BASE_URL}/get-all-assets`);
        if (Array.isArray(res.data.assets)) {
          setAssets(res.data.assets);
        }
      } catch (err) {
        console.error("Failed to load assets:", err);
      }
    };
    loadAssets();
  }, []);

  const buildEmailHtml = (recipientName = "there", recipientEmail = "") => `
  <html>
    <body style="margin:0;padding:0;background:#f3f5f9;font-family:Arial,Helvetica,sans-serif;">
      <table role="presentation" width="100%" cellspacing="0" cellpadding="0">
        <tr>
          <td align="center" style="padding:24px;">
            <table width="100%" style="max-width:680px;background:#ffffff;border-radius:16px;border:1px solid #e6e8f0;">
              <tr>
                <td style="background:#333999;text-align:center;padding:20px 24px;">
                  <img src="${logoUrl}" alt="Listernaut Logo" style="height:40px;background:#fff;padding:8px;border-radius:6px;" />
                  <div style="color:#D9DBFF;font-size:13px;margin-top:8px;">Official Announcement</div>
                </td>
              </tr>
              <tr>
                <td style="padding:30px;">
                  <p style="font-size:15px;color:#374151;line-height:1.6;">Hi <strong>${recipientName}</strong>,</p>
                  <div style="font-size:16px;line-height:1.6;color:#1f2937;margin-top:10px;white-space:pre-line;text-align:left;">
                    ${announcement || ""}
                  </div>
                  <p style="font-size:14px;color:#333999;text-align:center;margin-top:30px;">
                    — Team Listernaut
                  </p>
                </td>
              </tr>
              <tr>
                <td style="padding:20px;background:#ecf0f1;text-align:center;font-size:13px;color:#888;">
                  <p>Need help? Contact us at 
                    <a href="mailto:info@listernaut.com" style="color:#333999;text-decoration:underline;">info@listernaut.com</a>
                  </p>
                  <p style="margin:5px 0;"><a href="https://explorer.listernaut.com/unsubscribe?email=${encodeURIComponent(recipientEmail)}" style="color:#888;text-decoration:underline;">Unsubscribe</a></p>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </body>
  </html>`;

  const sendLnEmail = async () => {
    try {
      setSending(true);
      setSendResult(null);
      setErrorLog([]);

      const recipients = lnUsers.filter(u => u?.email && String(u.email).includes("@"));
      if (!recipients.length) {
        setSendResult({ ok: false, message: "No LN user emails found." });
        setSending(false);
        return;
      }

      setProgress({ sent: 0, total: recipients.length });

      for (let i = 0; i < recipients.length; i++) {
        const u = recipients[i];
        const html = buildEmailHtml(u.username || "there", u.email);
        try {
          await axios.post(`${config.API_BASE_URL}/send-email`, {
            to: [u.email],
            subject,
            html,
            text: announcement,
            fromName: "Listernaut",
            customArgs: announcementCustomArgs,
          });
        } catch (err) {
          const msg = err?.response?.data?.error || err?.message || "Failed for LN user";
          setErrorLog(prev => [...prev, { email: u.email, error: msg }]);
        }
        setProgress({ sent: i + 1, total: recipients.length });
      }

      setSendResult({ ok: true, message: `LN send completed ${recipients.length} sends. Check errors below.` });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "LN send failed.";
      setSendResult({ ok: false, message: msg });
    } finally {
      setSending(false);
    }
  };

  // Send to test group
  const sendTestEmail = async () => {
    const testRecipients = [
      "vrushab1998@gmail.com",
      "mark@listernaut.com",
      "logan@listernaut.com",
      "jeff@listernaut.com"
    ];
    try {
      setSending(true);
      setSendResult(null);
      const html = buildEmailHtml("there", "test@example.com");
      await axios.post(`${config.API_BASE_URL}/send-email`, {
        to: testRecipients,
        subject,
        html,
        text: announcement,
        fromName: "Listernaut",
        customArgs: announcementCustomArgs,
      });
      setSendResult({ ok: true, message: "✅ Test email sent successfully." });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "Failed to send test email.";
      setSendResult({ ok: false, message: msg });
    } finally {
      setSending(false);
    }
  };

  // Send to all users
  const sendEmail = async () => {
    try {
      setSending(true);
      setSendResult(null);
      setErrorLog([]);

      const recipients = users.filter(u => u?.email && String(u.email).includes("@"));
      if (!recipients.length) {
        setSendResult({ ok: false, message: "No valid user emails found." });
        setSending(false);
        return;
      }

      setProgress({ sent: 0, total: recipients.length });

      for (let i = 0; i < recipients.length; i++) {
        const u = recipients[i];
        const html = buildEmailHtml(u.username || "there", u.email);
        try {
          await axios.post(`${config.API_BASE_URL}/send-email`, {
            to: [u.email],
            subject,
            html,
            text: announcement,
            fromName: "Listernaut",
            customArgs: announcementCustomArgs,
          });
        } catch (err) {
          const msg = err?.response?.data?.error || err?.message || "Failed for user";
          setErrorLog(prev => [...prev, { email: u.email, error: msg }]);
        }
        setProgress({ sent: i + 1, total: recipients.length });
      }

      setSendResult({ ok: true, message: `Completed ${recipients.length} sends. Check errors below.` });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "Send failed.";
      setSendResult({ ok: false, message: msg });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-[#f3f5f9] min-h-screen flex flex-col items-center py-8 px-4 space-y-8">
      <div className="bg-white w-full max-w-4xl p-6 rounded-xl shadow border border-gray-200">
        <h2 className="text-2xl font-bold text-[#333999] mb-4">
          📢 Listernaut Announcements
        </h2>

        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full border rounded-md p-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
            <input
              type="url"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              className="w-full border rounded-md p-2 text-sm"
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Choose Existing Asset</label>
          <select
            className="w-full border rounded-md p-2 text-sm bg-white mb-3"
            onChange={(e) => {
              const url = e.target.value;
              if (url) {
                const imgHtml = `
<table role="presentation" width="100%" style="max-width:400px;margin:0 auto;">
  <tr>
   <td style="text-align:center; margin: 0 auto; mso-table-lspace:0pt; mso-table-rspace:0pt;">
      <img src="${url}" width="400" style="width:100%;height:auto;display:block;margin:0 auto;" />
    </td>
  </tr>
</table>
<p></p>`;
                setAnnouncement((prev) => prev + imgHtml);
              }
            }}
          >
            <option value="">-- Select an asset --</option>
            {assets.map((a, idx) => (
              <option key={idx} value={a.url}>
                {a.name.replace("assets/","")}
              </option>
            ))}
          </select>
          <label className="block text-sm font-medium text-gray-700 mb-1">Add Image</label>
          <input
            type="file"
            accept="image/*"
            onChange={async (e) => {
              const file = e.target.files[0];
              if (!file) return;

              const formData = new FormData();
              formData.append("file", file);

              try {
                const res = await axios.post(
                  `${config.API_BASE_URL}/upload-email-image`,
                  formData,
                  { headers: { "Content-Type": "multipart/form-data" } }
                );
                const url = res.data?.url;
                if (url) {
                  const imgHtml = `
<table role="presentation" width="100%" style="max-width:400px;margin:0 auto;">
  <tr>
    <td style="text-align:center;">
      <img src="${url}" width="400" style="width:100%;height:auto;display:block;margin:0 auto;" />
    </td>
  </tr>
</table>
<p></p>`;
                  setAnnouncement((prev) => prev + imgHtml);
                }
              } catch (err) {
                console.error("Image upload failed:", err);
                alert("Image upload failed.");
              }
            }}
            className="w-full border rounded-md p-2 text-sm bg-white"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Announcement</label>
          <ReactQuill
            value={announcement}
            onChange={setAnnouncement}
            theme="snow"
            className="bg-white"
            style={{ minHeight: "200px" }}
          />
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={sendEmail}
            disabled={sending}
            className={`px-4 py-2 rounded-md text-white ${sending ? "bg-gray-400" : "bg-[#333999] hover:opacity-90"}`}
          >
            {sending ? "Sending…" : `Send to All Users (${users.filter(u => u?.email).length})`}
          </button>
          <button
            onClick={sendLnEmail}
            disabled={sending}
            className={`px-4 py-2 rounded-md text-white ${sending ? "bg-gray-400" : "bg-blue-600 hover:opacity-90"}`}
          >
            {sending ? "Sending…" : `Send to LN Users (${lnUsers.filter(u => u?.email).length})`}
          </button>
          <button
            onClick={sendTestEmail}
            disabled={sending}
            className={`px-4 py-2 rounded-md text-white ${sending ? "bg-gray-400" : "bg-green-600 hover:opacity-90"}`}
          >
            {sending ? "Sending…" : "Send Test Email"}
          </button>
          {sendResult && (
            <span className={`text-sm ${sendResult.ok ? "text-green-600" : "text-red-600"}`}>
              {sendResult.message}
            </span>
          )}
          {progress.total > 0 && (
            <span className="text-xs text-gray-600">{progress.sent}/{progress.total} sent</span>
          )}
        </div>

        {errorLog.length > 0 && (
          <div className="mt-3 text-xs text-red-600 max-w-xl">
            <div className="font-semibold">Errors ({errorLog.length}):</div>
            <ul className="list-disc pl-5">
              {errorLog.slice(0, 10).map((e, idx) => (
                <li key={idx}>{e.email}: {e.error}</li>
              ))}
            </ul>
            {errorLog.length > 10 && <div>…and {errorLog.length - 10} more</div>}
          </div>
        )}
      </div>

      {/* Preview */}
      <div className="bg-white w-full max-w-3xl rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
        <div className="bg-[#333999] text-center py-6">
          <img src={logoUrl} alt="Listernaut Logo" className="h-10 mx-auto bg-white p-2 rounded-md mb-2" />
          <p className="text-[#D9DBFF] text-sm">Official Announcement</p>
        </div>
     <div
      className="p-8 text-gray-800"
      style={{ textAlign: "left" }}
      dangerouslySetInnerHTML={{ __html: announcement || "Type an announcement to preview it here..." }}
    ></div>
        <div className="bg-[#ecf0f1] text-center text-sm text-gray-600 py-6 border-t border-gray-300">
          <p>
            Contact us at{" "}
            <a href="mailto:info@listernaut.com" className="text-[#333999] underline">
              info@listernaut.com
            </a>
          </p>
          <p>&copy; {new Date().getFullYear()} Listernaut. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementsEmailEditor;
